import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Home, Building2, Settings, MapPin } from "lucide-react";
import { FiberHero } from "./Fiber";
import { Arrow, WaIcon } from "./Reveal";
import { WA_LINK } from "./data";

const ease = [0.16, 1, 0.3, 1];
const LINES = [
  { t: "Internet", c: "text-vnavy text-[52px] sm:text-7xl lg:text-[88px]" },
  { t: "Lebih Dekat", c: "text-gradient text-[52px] sm:text-7xl lg:text-[88px]" },
  { t: "untuk Hidup Lebih Baik", c: "text-vnavy text-[26px] sm:text-4xl lg:text-[42px] mt-1" },
];
const QUICK = [
  { icon: Home, l1: "Internet", l2: "Rumah", g: "from-sky-400 to-blue-700" },
  { icon: Building2, l1: "WiFi", l2: "Bisnis", g: "from-amber-400 to-orange-600" },
  { icon: Settings, l1: "Solusi", l2: "Jaringan", g: "from-emerald-400 to-teal-600" },
  { icon: MapPin, l1: "Area", l2: "Baru", g: "from-rose-400 to-red-600" },
];

const HeroImage = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div ref={ref} style={{ y, perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative">
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(12% 0% 12% 100% round 40px)", scale: 1.08 }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 40px)", scale: 1 }}
        transition={{ duration: 1.6, delay: 0.35, ease }}
        className="relative aspect-[4/3] lg:aspect-[5/4] rounded-[40px] overflow-hidden shadow-[0_50px_120px_-40px_rgba(11,42,138,0.7)] spotlight"
      >
        <img src="/img/car.jpg" alt="Mobil layanan VISION MEGA NETWORK" data-testid="hero-image" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-vnavy/50 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-3">
          <span className="glass-chip"><span className="live-dot" /> Jaringan aktif 24/7</span>
          <span className="glass-chip hidden sm:inline-flex">Fiber &amp; Satelit</span>
        </div>
      </motion.div>
      <motion.p
        data-testid="hero-tagline"
        initial={{ opacity: 0, y: 20, x: 20 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        transition={{ duration: 1.2, delay: 1.4, ease }}
        className="glass-tag font-tag uppercase text-right leading-[1.1] text-base sm:text-xl lg:text-2xl absolute -top-10 right-3 lg:-right-6"
      >
        <span className="block text-vnavy">Menghubungkan</span>
        <span className="block tag-gradient">Lebih Banyak</span>
        <span className="block tag-warm">Peluang</span>
      </motion.p>
    </motion.div>
  );
};

export const Hero = () => (
  <section id="beranda" data-testid="hero-section" className="relative overflow-hidden pt-32 sm:pt-36 pb-24 lg:pb-32 hero-bg">
    <FiberHero />
    <div className="grain" />
    <div className="relative max-w-[1320px] mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_1.15fr] gap-14 lg:gap-10 items-center">
      <div>
        <motion.span initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease }} className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-white px-4 py-1.5 text-xs font-bold tracking-widest text-vblue uppercase">
          <span className="live-dot" /> PT. Vision Mega Network
        </motion.span>
        <h1 data-testid="hero-title" className="font-display font-extrabold leading-[0.95] tracking-tight mt-6">
          {LINES.map((l, i) => (
            <span key={l.t} className="block overflow-hidden pb-1">
              <motion.span className={`block ${l.c}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease }}>
                {l.t}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease }} className="mt-6 text-slate-600 text-base lg:text-lg max-w-md leading-relaxed">
          <b className="text-vnavy">VISION MEGA NETWORK</b> menghadirkan layanan internet cepat, stabil dan terjangkau untuk rumah, usaha, sekolah dan seluruh daerah di Indonesia.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease }} className="mt-8 flex flex-wrap gap-3">
          <a href={WA_LINK} target="_blank" rel="noreferrer" data-testid="hero-consult-btn" className="btn-primary btn-shine"><WaIcon /> Konsultasi Sekarang</a>
          <a href="#layanan" data-testid="hero-packages-btn" className="btn-ghost">Lihat Paket <Arrow /></a>
        </motion.div>
        <div className="mt-10 grid grid-cols-4 gap-3 max-w-md">
          {QUICK.map((q, i) => (
            <motion.a href="#layanan" key={q.l2} data-testid={`hero-quick-${q.l2.toLowerCase()}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1 + i * 0.08, ease }} whileHover={{ y: -6 }} className="flex flex-col items-center text-center group">
              <span className={`w-14 h-14 rounded-2xl rotate-45 bg-gradient-to-br ${q.g} text-white grid place-items-center shadow-[0_14px_30px_-10px_rgba(11,42,138,0.6)] ring-4 ring-white transition-transform duration-500 group-hover:rotate-[135deg]`}>
                <q.icon size={24} strokeWidth={2.2} className="-rotate-45 transition-transform duration-500 group-hover:-rotate-[135deg]" />
              </span>
              <span className="mt-2 text-[12px] sm:text-[13px] font-bold text-vnavy leading-tight">{q.l1}<br />{q.l2}</span>
            </motion.a>
          ))}
        </div>
      </div>
      <div className="pt-14 lg:pt-0">
        <HeroImage />
      </div>
    </div>
  </section>
);
